import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Check, ChevronDown, Code, Copy, GitCommitHorizontal, Heading1, Heading2, Heading3, History, Italic, List, ListOrdered, Loader2, PanelRight, Plus, Quote, Redo, Strikethrough, Trash2, Undo } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { projectDataClient } from '../../../../lib/connect/clients';
import { SectionReviewStatus } from '../reviews/SectionReviewStatus';
import { SectionAuthoringRail } from './SectionAuthoringRail';
import { SourceChangeBanner } from './SourceChangeBanner';
import { isConcurrentEdit, readRevisedFile, textToDoc } from './sectionRevision';
import { VersionHistoryDrawer } from './VersionHistoryDrawer';
/**
 * SectionEditor — Tiptap rich text editor with git-like draft/commit workflow.
 *
 * Two rendering modes:
 *   - `bare=false` (default): full card with draft bar, toolbar, commit UI
 *   - `bare=true`: borderless editable surface only (for continuous doc layout)
 */ export function SectionEditor({ projectId, sectionId, bare = false, onFocus, onVersionSaved }) {
    const [saveStatus, setSaveStatus] = useState('idle');
    const [showHistory, setShowHistory] = useState(false);
    const [showCommitDialog, setShowCommitDialog] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [activeDraftId, setActiveDraftId] = useState('');
    const [draftsLoading, setDraftsLoading] = useState(true);
    const [showDraftMenu, setShowDraftMenu] = useState(false);
    const [showRail, setShowRail] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    // The reloadKey whose drafts have arrived: the editor restarts on this, not on the request.
    const [loadedKey, setLoadedKey] = useState(0);
    const [conflict, setConflict] = useState(null);
    const [entryStatus, setEntryStatus] = useState('');
    const [entryBusy, setEntryBusy] = useState(false);
    const isMountedRef = useRef(true);
    // The head version the editor is based on; saves are refused if it moved.
    const headRef = useRef(undefined);
    const pendingRef = useRef(null);
    const debounceRef = useRef(null);
    useEffect(()=>{
        isMountedRef.current = true;
        return ()=>{
            isMountedRef.current = false;
        };
    }, []);
    // Load drafts on mount.
    useEffect(()=>{
        let cancelled = false;
        async function loadDrafts() {
            try {
                const resp = await projectDataClient.listDrafts({
                    projectId,
                    sectionId
                });
                if (cancelled) return;
                const loaded = (resp.drafts ?? []).map((d)=>({
                        draftId: d.draftId,
                        name: d.name,
                        contentJson: d.contentJson,
                        contentText: d.contentText,
                        updatedBy: d.updatedBy,
                        headVersionId: d.headVersionId
                    }));
                headRef.current = loaded.find((d)=>d.name === 'Main')?.headVersionId ?? '';
                setDrafts(loaded);
                setLoadedKey(reloadKey);
                if (loaded.length > 0 && !activeDraftId) {
                    setActiveDraftId(loaded[0].draftId);
                }
            } catch  {
            // No drafts yet — will be created on first save.
            } finally{
                if (!cancelled) setDraftsLoading(false);
            }
        }
        loadDrafts();
        return ()=>{
            cancelled = true;
        };
    }, [
        projectId,
        sectionId,
        activeDraftId,
        reloadKey
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    const activeDraft = useMemo(()=>drafts.find((d)=>d.draftId === activeDraftId), [
        drafts,
        activeDraftId
    ]);
    const activeIsMain = !activeDraft || activeDraft.name === 'Main';
    // Save to draft (autosave target). force writes on top of whatever is there.
    const saveDraft = useCallback(async (json, text, force = false)=>{
        if (!projectId || !sectionId) return false;
        setSaveStatus('saving');
        pendingRef.current = {
            json,
            text
        };
        try {
            const resp = await projectDataClient.saveDraft({
                projectId,
                sectionId,
                draftId: activeDraftId,
                contentJson: json,
                contentText: text,
                ...activeIsMain && !force && headRef.current !== undefined ? {
                    baseVersionId: headRef.current
                } : {}
            });
            pendingRef.current = null;
            if (activeIsMain) headRef.current = resp.headVersionId;
            if (isMountedRef.current) {
                setSaveStatus('saved');
                if (resp.draftId && !activeDraftId) {
                    setActiveDraftId(resp.draftId);
                    setDrafts((prev)=>prev.length === 0 ? [
                            {
                                draftId: resp.draftId,
                                name: 'Main',
                                contentJson: json,
                                contentText: text,
                                updatedBy: '',
                                headVersionId: resp.headVersionId
                            }
                        ] : prev);
                }
                setTimeout(()=>{
                    if (isMountedRef.current) setSaveStatus('idle');
                }, 2000);
            }
            return true;
        } catch (err) {
            if (!isMountedRef.current) return false;
            if (isConcurrentEdit(err)) {
                setSaveStatus('idle');
                setConflict({
                    overwrite: async ()=>void await saveDraft(json, text, true)
                });
                return false;
            }
            setSaveStatus('error');
            console.error('Failed to save draft:', err);
            return false;
        }
    }, [
        projectId,
        sectionId,
        activeDraftId,
        activeIsMain
    ]);
    // Make the editor show the latest saved text (after a restore, import or conflict).
    const reload = useCallback(()=>{
        pendingRef.current = null;
        setConflict(null);
        setReloadKey((k)=>k + 1);
    }, []);
    const flushPending = useCallback(async ()=>{
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const p = pendingRef.current;
        return p ? saveDraft(p.json, p.text) : true;
    }, [
        saveDraft
    ]);
    const importRevision = useCallback(async (fileName, text, force = false)=>{
        const doc = textToDoc(text);
        if (doc.text === '') {
            setEntryStatus('There is no text to bring in.');
            return;
        }
        setEntryBusy(true);
        try {
            if (!await flushPending()) return;
            const resp = await projectDataClient.importSectionRevision({
                projectId,
                sectionId,
                fileName,
                contentJson: doc.json,
                contentText: doc.text,
                ...!force && headRef.current !== undefined ? {
                    baseVersionId: headRef.current
                } : {}
            });
            setEntryStatus(fileName ? `Added ${fileName} as revision v${resp.version}.` : `Added pasted text as revision v${resp.version}.`);
            reload();
        } catch (err) {
            if (isConcurrentEdit(err)) {
                setConflict({
                    overwrite: ()=>importRevision(fileName, text, true)
                });
            } else {
                console.error('Failed to import revision:', err);
                setEntryStatus("Couldn't add that text. Nothing was changed.");
            }
        } finally{
            setEntryBusy(false);
        }
    }, [
        projectId,
        sectionId,
        flushPending,
        reload
    ]);
    const uploadRevisedFile = useCallback(async (file)=>{
        try {
            await importRevision(file.name, await readRevisedFile(file));
        } catch  {
            setEntryStatus(`Couldn't read ${file.name}.`);
        }
    }, [
        importRevision
    ]);
    const debouncedSave = useCallback((json, text)=>{
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(()=>saveDraft(json, text), 1000);
    }, [
        saveDraft
    ]);
    // Parse initial content from the active draft.
    let parsedContent;
    const initialJson = activeDraft?.contentJson;
    if (initialJson) {
        try {
            parsedContent = JSON.parse(initialJson);
        } catch  {
        // Invalid JSON — start empty.
        }
    }
    const editorClass = bare ? 'prose prose-sm max-w-none focus:outline-none min-h-[2em]' : 'prose prose-sm max-w-none focus:outline-none min-h-[300px] px-5 py-4';
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [
                        1,
                        2,
                        3
                    ]
                }
            }),
            Highlight,
            Link.configure({
                openOnClick: false
            }),
            Placeholder.configure({
                placeholder: bare ? 'Start writing...' : 'Start writing your response...'
            }),
            Table.configure({
                resizable: true
            }),
            TableRow,
            TableHeader,
            TableCell
        ],
        content: parsedContent || {
            type: 'doc',
            content: [
                {
                    type: 'paragraph'
                }
            ]
        },
        editorProps: {
            attributes: {
                class: editorClass,
                'data-testid': Testids.proposalStructure.editor
            },
            handleKeyDown: (_view, event)=>{
                const e = event;
                const isMod = navigator.platform.includes('Mac') ? e.metaKey : e.ctrlKey;
                if (isMod && (e.key === 's' || e.key === 'S')) {
                    e.preventDefault();
                    const json = JSON.stringify(editor?.getJSON());
                    const text = editor?.getText() || '';
                    saveDraft(json, text);
                    return true;
                }
                return false;
            }
        },
        onUpdate: ({ editor: ed })=>{
            const json = JSON.stringify(ed.getJSON());
            const text = ed.getText();
            debouncedSave(json, text);
        },
        onFocus: ({ editor: ed })=>{
            onFocus?.(ed);
        },
        immediatelyRender: false
    }, [
        activeDraftId,
        loadedKey
    ]);
    // -- Draft actions --
    const handleCreateDraft = async (name, forkFromDraftId)=>{
        try {
            const resp = await projectDataClient.createDraft({
                projectId,
                sectionId,
                name,
                forkFromDraftId: forkFromDraftId || ''
            });
            const newDraft = resp.draft;
            if (newDraft) {
                const info = {
                    draftId: newDraft.draftId,
                    name: newDraft.name,
                    contentJson: newDraft.contentJson,
                    contentText: newDraft.contentText,
                    updatedBy: newDraft.updatedBy,
                    headVersionId: newDraft.headVersionId
                };
                setDrafts((prev)=>[
                        ...prev,
                        info
                    ]);
                setActiveDraftId(info.draftId);
            }
        } catch (err) {
            console.error('Failed to create draft:', err);
        }
    };
    const handleDeleteDraft = async (draftId)=>{
        if (drafts.length <= 1) return;
        try {
            await projectDataClient.deleteDraft({
                projectId,
                draftId
            });
            setDrafts((prev)=>prev.filter((d)=>d.draftId !== draftId));
            if (activeDraftId === draftId) {
                const remaining = drafts.filter((d)=>d.draftId !== draftId);
                setActiveDraftId(remaining[0]?.draftId || '');
            }
        } catch (err) {
            console.error('Failed to delete draft:', err);
        }
    };
    const handleSwitchDraft = (draftId)=>{
        setActiveDraftId(draftId);
        setShowDraftMenu(false);
    };
    const handleCommitForced = async (message)=>{
        const resp = await projectDataClient.commitDraft({
            projectId,
            sectionId,
            draftId: activeDraftId,
            message
        });
        onVersionSaved?.(resp.version);
        reload();
    };
    const handleCommit = async (message)=>{
        if (!activeDraftId) return;
        try {
            if (editor) {
                const json = JSON.stringify(editor.getJSON());
                const text = editor.getText();
                if (!await saveDraft(json, text)) return;
            }
            const resp = await projectDataClient.commitDraft({
                projectId,
                sectionId,
                draftId: activeDraftId,
                message
            });
            onVersionSaved?.(resp.version);
            setShowCommitDialog(false);
        } catch (err) {
            if (isConcurrentEdit(err)) {
                setShowCommitDialog(false);
                setConflict({
                    overwrite: async ()=>handleCommitForced(message)
                });
                return;
            }
            console.error('Failed to commit:', err);
        }
    };
    if (draftsLoading || !editor) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-64 items-center justify-center",
            children: /*#__PURE__*/ _jsx(Loader2, {
                className: "h-5 w-5 animate-spin text-slate-400"
            })
        });
    }
    // --- Bare mode: editable surface + inline action strip on hover ---
    if (bare) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "group/section relative",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "absolute -top-6 left-0 z-10",
                    children: /*#__PURE__*/ _jsx(SectionReviewStatus, {
                        projectId: projectId,
                        sectionId: sectionId
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "absolute -top-6 right-0 z-10 flex items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 shadow-sm opacity-0 transition-opacity group-hover/section:opacity-100",
                    children: [
                        drafts.length > 0 && /*#__PURE__*/ _jsxs("span", {
                            className: "inline-flex items-center gap-1 text-[10px] text-slate-500",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "h-1.5 w-1.5 rounded-full bg-emerald-500"
                                }),
                                activeDraft?.name ?? 'Main'
                            ]
                        }),
                        drafts.length > 1 && /*#__PURE__*/ _jsx("select", {
                            value: activeDraftId,
                            onChange: (e)=>{
                                setActiveDraftId(e.target.value);
                            },
                            className: "h-5 rounded border-0 bg-transparent px-0.5 text-[10px] text-slate-600 focus:outline-none focus:ring-0",
                            children: drafts.map((d)=>/*#__PURE__*/ _jsx("option", {
                                    value: d.draftId,
                                    children: d.name
                                }, d.draftId))
                        }),
                        /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            "data-testid": Testids.proposalVersions.commitButton,
                            onClick: ()=>setShowCommitDialog(true),
                            className: "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50",
                            title: "Commit this section",
                            children: [
                                /*#__PURE__*/ _jsx(GitCommitHorizontal, {
                                    className: "h-3 w-3"
                                }),
                                "Commit"
                            ]
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            "data-testid": Testids.proposalVersions.historyButton,
                            onClick: ()=>setShowHistory(true),
                            className: "rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground",
                            title: "Version history",
                            children: /*#__PURE__*/ _jsx(History, {
                                className: "h-3 w-3"
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "link",
                            size: "inline",
                            type: "button",
                            "data-testid": Testids.proposalVersions.railToggle,
                            onClick: ()=>setShowRail((v)=>!v),
                            "aria-pressed": showRail,
                            className: "rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground",
                            title: "Outline, sources and text entry",
                            children: /*#__PURE__*/ _jsx(PanelRight, {
                                className: "h-3 w-3"
                            })
                        }),
                        saveStatus === 'saving' && /*#__PURE__*/ _jsx(Loader2, {
                            className: "h-3 w-3 animate-spin text-muted-foreground"
                        }),
                        saveStatus === 'saved' && /*#__PURE__*/ _jsx(Check, {
                            className: "h-3 w-3 text-emerald-500"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("output", {
                    "data-testid": Testids.proposalStructure.saveStatus,
                    className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                    children: saveStatus === 'saving' ? 'Saving draft…' : saveStatus === 'saved' ? 'Draft saved' : saveStatus === 'error' ? 'Save failed — keep this page open and try saving again.' : ''
                }),
                /*#__PURE__*/ _jsx(SourceChangeBanner, {
                    projectId: projectId,
                    sectionId: sectionId,
                    refreshKey: reloadKey,
                    beforeAcknowledge: flushPending,
                    onAcknowledged: reload
                }),
                conflict && /*#__PURE__*/ _jsx(ConflictBanner, {
                    onReload: reload,
                    onOverwrite: async ()=>{
                        const c = conflict;
                        setConflict(null);
                        await c.overwrite();
                        reload();
                    },
                    onReview: ()=>setShowHistory(true)
                }),
                /*#__PURE__*/ _jsx(EditorContent, {
                    editor: editor
                }),
                showRail && /*#__PURE__*/ _jsx("div", {
                    className: "absolute left-full top-0 z-20 ml-4",
                    children: /*#__PURE__*/ _jsx(SectionAuthoringRail, {
                        projectId: projectId,
                        sectionId: sectionId,
                        busy: entryBusy,
                        entryStatus: entryStatus,
                        onPaste: (t)=>importRevision('', t),
                        onUploadFile: uploadRevisedFile,
                        onOpenHistory: ()=>setShowHistory(true)
                    })
                }),
                /*#__PURE__*/ _jsx(VersionHistoryDrawer, {
                    isOpen: showHistory,
                    onClose: ()=>setShowHistory(false),
                    projectId: projectId,
                    entityType: "section",
                    entityId: sectionId,
                    onRestore: reload,
                    beforeChange: async ()=>void await flushPending()
                }),
                showCommitDialog && /*#__PURE__*/ _jsx(CommitDialog, {
                    onCommit: handleCommit,
                    onClose: ()=>setShowCommitDialog(false),
                    draftName: activeDraft?.name ?? 'Main'
                })
            ]
        });
    }
    // --- Full mode: card with draft bar, toolbar, commit UI ---
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-xl border border-slate-200 bg-white shadow-sm",
        children: [
            /*#__PURE__*/ _jsx(DraftBar, {
                drafts: drafts,
                activeDraftId: activeDraftId,
                showMenu: showDraftMenu,
                onToggleMenu: ()=>setShowDraftMenu((v)=>!v),
                onSwitch: handleSwitchDraft,
                onCreateDraft: handleCreateDraft,
                onDeleteDraft: handleDeleteDraft,
                onCommit: ()=>setShowCommitDialog(true)
            }),
            /*#__PURE__*/ _jsx(EditorToolbar, {
                editor: editor,
                saveStatus: saveStatus,
                onShowHistory: ()=>setShowHistory(true)
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between border-b border-border px-2 py-1",
                children: [
                    /*#__PURE__*/ _jsx(SectionReviewStatus, {
                        projectId: projectId,
                        sectionId: sectionId
                    }),
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "link",
                        size: "inline",
                        type: "button",
                        "data-testid": Testids.proposalVersions.railToggle,
                        onClick: ()=>setShowRail((v)=>!v),
                        "aria-pressed": showRail,
                        className: "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[length:var(--fs-ui-meta)] text-muted-foreground hover:bg-muted",
                        children: [
                            /*#__PURE__*/ _jsx(PanelRight, {
                                className: "h-3 w-3"
                            }),
                            showRail ? 'Hide outline & sources' : 'Outline, sources & text entry'
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SourceChangeBanner, {
                projectId: projectId,
                sectionId: sectionId,
                refreshKey: reloadKey,
                beforeAcknowledge: flushPending,
                onAcknowledged: reload
            }),
            conflict && /*#__PURE__*/ _jsx(ConflictBanner, {
                onReload: reload,
                onOverwrite: async ()=>{
                    const c = conflict;
                    setConflict(null);
                    await c.overwrite();
                    reload();
                },
                onReview: ()=>setShowHistory(true)
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start gap-3",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "min-w-0 flex-1",
                        children: /*#__PURE__*/ _jsx(EditorContent, {
                            editor: editor
                        })
                    }),
                    showRail && /*#__PURE__*/ _jsx(SectionAuthoringRail, {
                        projectId: projectId,
                        sectionId: sectionId,
                        busy: entryBusy,
                        entryStatus: entryStatus,
                        onPaste: (t)=>importRevision('', t),
                        onUploadFile: uploadRevisedFile,
                        onOpenHistory: ()=>setShowHistory(true)
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(VersionHistoryDrawer, {
                isOpen: showHistory,
                onClose: ()=>setShowHistory(false),
                projectId: projectId,
                entityType: "section",
                entityId: sectionId,
                onRestore: reload,
                beforeChange: async ()=>void await flushPending()
            }),
            showCommitDialog && /*#__PURE__*/ _jsx(CommitDialog, {
                onCommit: handleCommit,
                onClose: ()=>setShowCommitDialog(false),
                draftName: activeDraft?.name ?? 'Main'
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Shared Toolbar — exported so the continuous doc layout can render one
// floating toolbar for whichever section is focused
// ---------------------------------------------------------------------------
export function EditorToolbar({ editor, saveStatus, onShowHistory }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-0.5 border-b border-slate-200 px-2 py-1.5",
        children: [
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().toggleHeading({
                        level: 1
                    }).run(),
                active: editor.isActive('heading', {
                    level: 1
                }),
                title: "Heading 1",
                children: /*#__PURE__*/ _jsx(Heading1, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().toggleHeading({
                        level: 2
                    }).run(),
                active: editor.isActive('heading', {
                    level: 2
                }),
                title: "Heading 2",
                children: /*#__PURE__*/ _jsx(Heading2, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().toggleHeading({
                        level: 3
                    }).run(),
                active: editor.isActive('heading', {
                    level: 3
                }),
                title: "Heading 3",
                children: /*#__PURE__*/ _jsx(Heading3, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(ToolbarDivider, {}),
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().toggleBold().run(),
                active: editor.isActive('bold'),
                title: "Bold (Cmd+B)",
                children: /*#__PURE__*/ _jsx(Bold, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().toggleItalic().run(),
                active: editor.isActive('italic'),
                title: "Italic (Cmd+I)",
                children: /*#__PURE__*/ _jsx(Italic, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().toggleStrike().run(),
                active: editor.isActive('strike'),
                title: "Strikethrough",
                children: /*#__PURE__*/ _jsx(Strikethrough, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().toggleCode().run(),
                active: editor.isActive('code'),
                title: "Code",
                children: /*#__PURE__*/ _jsx(Code, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(ToolbarDivider, {}),
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().toggleBulletList().run(),
                active: editor.isActive('bulletList'),
                title: "Bullet list",
                children: /*#__PURE__*/ _jsx(List, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().toggleOrderedList().run(),
                active: editor.isActive('orderedList'),
                title: "Numbered list",
                children: /*#__PURE__*/ _jsx(ListOrdered, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().toggleBlockquote().run(),
                active: editor.isActive('blockquote'),
                title: "Quote",
                children: /*#__PURE__*/ _jsx(Quote, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(ToolbarDivider, {}),
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().undo().run(),
                disabled: !editor.can().undo(),
                title: "Undo (Cmd+Z)",
                children: /*#__PURE__*/ _jsx(Undo, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(ToolbarButton, {
                onClick: ()=>editor.chain().focus().redo().run(),
                disabled: !editor.can().redo(),
                title: "Redo (Cmd+Shift+Z)",
                children: /*#__PURE__*/ _jsx(Redo, {
                    className: "h-4 w-4"
                })
            }),
            onShowHistory && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(ToolbarDivider, {}),
                    /*#__PURE__*/ _jsx(ToolbarButton, {
                        onClick: onShowHistory,
                        title: "Version history",
                        children: /*#__PURE__*/ _jsx(History, {
                            className: "h-4 w-4"
                        })
                    })
                ]
            }),
            saveStatus && /*#__PURE__*/ _jsxs("div", {
                className: "ml-auto flex items-center gap-1.5 px-2",
                children: [
                    saveStatus === 'saving' && /*#__PURE__*/ _jsxs("span", {
                        className: "flex items-center gap-1 text-[11px] text-slate-400",
                        children: [
                            /*#__PURE__*/ _jsx(Loader2, {
                                className: "h-3 w-3 animate-spin"
                            }),
                            " Saving..."
                        ]
                    }),
                    saveStatus === 'saved' && /*#__PURE__*/ _jsxs("span", {
                        className: "flex items-center gap-1 text-[11px] text-emerald-600",
                        children: [
                            /*#__PURE__*/ _jsx(Check, {
                                className: "h-3 w-3"
                            }),
                            " Draft saved"
                        ]
                    }),
                    saveStatus === 'error' && /*#__PURE__*/ _jsx("span", {
                        className: "text-[11px] text-rose-600",
                        children: "Save failed"
                    })
                ]
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Draft Bar
// ---------------------------------------------------------------------------
function DraftBar({ drafts, activeDraftId, showMenu, onToggleMenu, onSwitch, onCreateDraft, onDeleteDraft, onCommit }) {
    const active = drafts.find((d)=>d.draftId === activeDraftId);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-2 border-b border-slate-200 bg-slate-50/80 px-3 py-1.5",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "relative",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        onClick: onToggleMenu,
                        className: "inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "h-2 w-2 rounded-full bg-emerald-500"
                            }),
                            active?.name ?? 'Main',
                            drafts.length > 1 && /*#__PURE__*/ _jsxs("span", {
                                className: "text-[10px] text-slate-400",
                                children: [
                                    "(",
                                    drafts.length,
                                    ")"
                                ]
                            }),
                            /*#__PURE__*/ _jsx(ChevronDown, {
                                className: "h-3 w-3 text-slate-400"
                            })
                        ]
                    }),
                    showMenu && /*#__PURE__*/ _jsxs("div", {
                        className: "absolute left-0 top-full z-30 mt-1 min-w-[200px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg",
                        children: [
                            drafts.map((d)=>/*#__PURE__*/ _jsxs("div", {
                                    className: `flex items-center gap-2 px-3 py-1.5 text-xs transition-colors hover:bg-slate-50 ${d.draftId === activeDraftId ? 'bg-blue-50 text-blue-900' : 'text-slate-700'}`,
                                    children: [
                                        /*#__PURE__*/ _jsxs("button", {
                                            type: "button",
                                            onClick: ()=>onSwitch(d.draftId),
                                            className: "flex min-w-0 flex-1 items-center gap-1.5",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: `h-2 w-2 flex-shrink-0 rounded-full ${d.draftId === activeDraftId ? 'bg-emerald-500' : 'bg-slate-300'}`
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "truncate",
                                                    children: d.name
                                                })
                                            ]
                                        }),
                                        drafts.length > 1 && /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: (e)=>{
                                                e.stopPropagation();
                                                if (confirm(`Delete draft "${d.name}"?`)) {
                                                    onDeleteDraft(d.draftId);
                                                }
                                            },
                                            className: "flex-shrink-0 rounded p-0.5 text-slate-300 hover:bg-rose-50 hover:text-rose-500",
                                            title: "Delete draft",
                                            children: /*#__PURE__*/ _jsx(Trash2, {
                                                className: "h-3 w-3"
                                            })
                                        })
                                    ]
                                }, d.draftId)),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "border-t border-slate-100 px-1 py-1",
                                children: [
                                    /*#__PURE__*/ _jsxs("button", {
                                        type: "button",
                                        onClick: ()=>{
                                            const name = prompt('Draft name:');
                                            if (name?.trim()) {
                                                onCreateDraft(name.trim(), activeDraftId);
                                            }
                                        },
                                        className: "flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50",
                                        children: [
                                            /*#__PURE__*/ _jsx(Plus, {
                                                className: "h-3 w-3"
                                            }),
                                            " New draft"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("button", {
                                        type: "button",
                                        onClick: ()=>{
                                            const name = prompt('Name for the fork:', `${active?.name ?? 'Main'} (copy)`);
                                            if (name?.trim()) {
                                                onCreateDraft(name.trim(), activeDraftId);
                                            }
                                        },
                                        className: "flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50",
                                        children: [
                                            /*#__PURE__*/ _jsx(Copy, {
                                                className: "h-3 w-3"
                                            }),
                                            " Fork current draft"
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "ml-auto",
                children: /*#__PURE__*/ _jsxs("button", {
                    type: "button",
                    onClick: onCommit,
                    className: "inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800 transition-colors hover:bg-emerald-100",
                    title: "Create a committed version from this draft",
                    children: [
                        /*#__PURE__*/ _jsx(GitCommitHorizontal, {
                            className: "h-3.5 w-3.5"
                        }),
                        "Commit"
                    ]
                })
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Commit Dialog
// ---------------------------------------------------------------------------
function ConflictBanner({ onReload, onOverwrite, onReview }) {
    return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        "data-testid": Testids.proposalVersions.conflictBanner,
        className: "border-b border-warning bg-warning-muted px-4 py-2 text-xs text-warning",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "font-semibold",
                children: "Someone else changed this section while you were editing."
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "mt-0.5",
                children: "Your last change has not been saved, so nothing of theirs was overwritten. Choose how to continue:"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-1.5 flex flex-wrap gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "outline",
                        size: "sm",
                        type: "button",
                        "data-testid": Testids.proposalVersions.conflictReview,
                        onClick: onReview,
                        children: "Compare in history"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "outline",
                        size: "sm",
                        type: "button",
                        "data-testid": Testids.proposalVersions.conflictReload,
                        onClick: onReload,
                        children: "Load their version (discard my change)"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "default",
                        size: "sm",
                        type: "button",
                        "data-testid": Testids.proposalVersions.conflictOverwrite,
                        onClick: onOverwrite,
                        children: "Keep my change on top of theirs"
                    })
                ]
            })
        ]
    });
}
function CommitDialog({ onCommit, onClose, draftName }) {
    const [message, setMessage] = useState('');
    const [committing, setCommitting] = useState(false);
    const handleSubmit = async ()=>{
        setCommitting(true);
        await onCommit(message.trim());
        setCommitting(false);
    };
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 z-50 flex items-center justify-center bg-black/40",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(GitCommitHorizontal, {
                            className: "h-5 w-5 text-emerald-600"
                        }),
                        /*#__PURE__*/ _jsx("h3", {
                            className: "text-sm font-semibold text-slate-900",
                            children: "Commit version"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("p", {
                    className: "mt-1.5 text-xs text-slate-500",
                    children: [
                        "Create an immutable snapshot from draft ",
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-semibold",
                            children: draftName
                        }),
                        ". This will appear in the version history."
                    ]
                }),
                /*#__PURE__*/ _jsx("textarea", {
                    value: message,
                    onChange: (e)=>setMessage(e.target.value),
                    placeholder: "What changed? (optional)",
                    className: "mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-100",
                    rows: 2,
                    onKeyDown: (e)=>{
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            handleSubmit();
                        }
                    }
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "mt-3 flex items-center justify-end gap-2",
                    children: [
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: onClose,
                            className: "rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100",
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            onClick: handleSubmit,
                            disabled: committing,
                            className: "inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50",
                            children: [
                                committing ? /*#__PURE__*/ _jsx(Loader2, {
                                    className: "h-3.5 w-3.5 animate-spin"
                                }) : /*#__PURE__*/ _jsx(GitCommitHorizontal, {
                                    className: "h-3.5 w-3.5"
                                }),
                                "Commit"
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
// ---------------------------------------------------------------------------
// Shared toolbar primitives
// ---------------------------------------------------------------------------
function ToolbarButton({ onClick, active, disabled, title, children }) {
    return /*#__PURE__*/ _jsx("button", {
        type: "button",
        onClick: onClick,
        disabled: disabled,
        title: title,
        className: `rounded p-1.5 transition-colors ${active ? 'bg-slate-200 text-slate-900' : disabled ? 'text-slate-300' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`,
        children: children
    });
}
function ToolbarDivider() {
    return /*#__PURE__*/ _jsx("div", {
        className: "mx-1 h-5 w-px bg-slate-200"
    });
}
